import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import { useEffect, useState } from "react";
import { FieldErrors, FieldValues, UseFormHandleSubmit } from "react-hook-form";
import { UseQueryResult } from "react-query/types/react/types";

import { APQueryOptions } from "../providers/APQueryProvider";
import { PaginationFilters, PaginationResponse } from "../types/Pagination";
import APForm from "./form/APForm";
import PageContainer from "./PageContainer";
import PaginationContainer from "./PaginationContainer";

type APFilterableListProps<
  TInputs extends FieldValues,
  TFilters extends PaginationFilters,
  TItem
> = {
  title: string;
  filters: TFilters;
  setFilters: (filters: TFilters) => void;
  pageSize: number;
  getItems: (
    filters: TFilters,
    options: APQueryOptions
  ) => UseQueryResult<PaginationResponse<TItem>, unknown>;
  inputsToFilters: (inputs: TInputs) => TFilters;
  handleSubmit: UseFormHandleSubmit<TInputs, undefined>;
  errors: FieldErrors<TInputs>;
  formFields: React.ReactNode;
  renderItems: (items: TItem[], paginationHistory: string[]) => React.ReactNode;
  isLoading: boolean;
  isSuccess: boolean;
  aboveTitleContent?: React.ReactNode;
  topCollapseContent?: React.ReactNode;
  abovePaginationContent?: React.ReactNode;
  overrideContent?: React.ReactNode;
  onFilterSubmit?: (inputs: TInputs) => void;
  bottomContent?: React.ReactNode;
  shouldDisplayEmptyMessage?: boolean;
  onPageLoad?: (items: TItem[]) => void;
  twoColumnLayout?: boolean;
  autoExpandFiltersOnMultiPage?: boolean;
  hideFilters?: boolean;
};

const APFilterableList = <
  TInputs extends FieldValues,
  TFilters extends PaginationFilters,
  TItem
>({
  title,
  filters,
  setFilters,
  pageSize,
  getItems,
  inputsToFilters,
  handleSubmit,
  errors,
  formFields,
  renderItems,
  isLoading,
  isSuccess,
  aboveTitleContent,
  topCollapseContent,
  abovePaginationContent,
  overrideContent,
  onFilterSubmit,
  bottomContent,
  shouldDisplayEmptyMessage,
  onPageLoad,
  twoColumnLayout = false,
  autoExpandFiltersOnMultiPage = false,
  hideFilters = false,
}: APFilterableListProps<TInputs, TFilters, TItem>) => {
  const [showFilters, setShowFilters] = useState(false);
  const [paginationHistory, setPaginationHistory] = useState<string[]>([]);
  const [pendingPage, setPendingPage] = useState<{
    nextPageLink?: string;
    history: string[];
  } | null>(null);

  const queryFilters = pendingPage
    ? { ...filters, nextPageLink: pendingPage.nextPageLink }
    : filters;

  const items = getItems(queryFilters, {
    keepPreviousData: true,
  });

  if (items.isError) {
    throw items.error;
  }

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [filters]);

  useEffect(() => {
    if (items.isSuccess && onPageLoad) {
      onPageLoad(items.data?.items || []);
    }
  }, [items]);

  useEffect(() => {
    if (pendingPage && items.isSuccess && !items.isFetching) {
      setFilters(queryFilters);
      setPaginationHistory(pendingPage.history);
      setPendingPage(null);
    }
  }, [pendingPage, items.isFetching, items.isSuccess]);

  useEffect(() => {
    if (autoExpandFiltersOnMultiPage && items.isSuccess) {
      if (items.data?.count && items.data?.count > pageSize) {
        setShowFilters(true);
      }
    }
  }, [items.isSuccess, autoExpandFiltersOnMultiPage]);

  const handleFiltersClick = () => {
    setShowFilters(!showFilters);
  };

  const handlePageChange = (
    nextPageLink: string | undefined,
    paginationHistory: string[]
  ) => {
    setPendingPage({ nextPageLink, history: paginationHistory });
  };

  const handleSubmitFilters = (inputs: TInputs) => {
    setFilters({ ...inputsToFilters(inputs), nextPageLink: undefined });
    setPaginationHistory([]);
    setShowFilters(false);
    onFilterSubmit?.(inputs);
  };

  return (
    <PageContainer
      title={title}
      loading={isLoading || (items.isLoading && !pendingPage)}
      titleButton={
        !hideFilters && (
          <IconButton
            size="large"
            aria-label="refresh"
            color="onSurface"
            onClick={handleFiltersClick}
          >
            <SearchOutlinedIcon />
          </IconButton>
        )
      }
      aboveTitleContent={aboveTitleContent}
    >
      {isSuccess && (
        <>
          <Box>
            {topCollapseContent}
            <Collapse in={showFilters}>
              <Box mb={2}>
                <APForm
                  onSubmit={handleSubmit(handleSubmitFilters)}
                  submitText={"Go"}
                  isLoading={false}
                  isError={Object.keys(errors).length > 0}
                  minSpacing
                  submitButtonColor="primary"
                >
                  {formFields}
                </APForm>
              </Box>
            </Collapse>
            {abovePaginationContent && (
              <Box mb={1}>{abovePaginationContent}</Box>
            )}
          </Box>
          {items.isSuccess && !overrideContent && (
            <PaginationContainer
              currentPage={paginationHistory.length + 1}
              pageSize={pageSize}
              itemCount={items.data?.count || 0}
              onPageChange={handlePageChange}
              paginationHistory={paginationHistory}
              nextPageLink={items.data?.nextPageLink || ""}
              displayEmptyMessage={shouldDisplayEmptyMessage}
              isLoading={items.isFetching}
            >
              <Stack
                spacing={1}
                sx={{
                  ...(twoColumnLayout
                    ? {
                        display: "grid",
                        gap: "16px",
                        gridTemplateColumns: { md: "1fr 1fr" },
                      }
                    : {}),
                }}
              >
                {renderItems(items.data?.items || [], paginationHistory)}
              </Stack>
            </PaginationContainer>
          )}
          {overrideContent}
        </>
      )}
      {bottomContent}
    </PageContainer>
  );
};

export default APFilterableList;
